/**
 * Demo agencies only: a "View as" drop-down so a demo can jump between deputy, supervisor, admin and the
 * rest without signing out and back in. Each choice is a real demo login with its own permissions.
 */
import { useEffect, useState } from 'react';
import { api } from './api.ts';
import { JUST_SIGNED_IN } from './login.tsx';

interface DemoRoles {
  demo: boolean;
  roles: { key: string; label: string; name: string | null }[];
  current: string | null;
}

export function DemoSwitch() {
  const [data, setData] = useState<DemoRoles | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    api.get<DemoRoles>('/api/demo/roles').then(setData, () => setData(null));
  }, []);
  useEffect(() => {
    document.body.classList.toggle('demo-on', !!data?.demo);
    document.documentElement.classList.toggle('demo-on', !!data?.demo);
    return () => (document.body.classList.remove('demo-on'), document.documentElement.classList.remove('demo-on'));
  }, [data?.demo]);
  if (!data?.demo) return null;

  const change = async (key: string) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post<{ role: string; same?: boolean }>('/api/demo/switch', { role: key });
      if (r.same) return setBusy(false);
      const { purgeOfflineData } = await import('../field/store.ts');
      await purgeOfflineData().catch(() => undefined);
      try {
        sessionStorage.removeItem('civilos.me');
        sessionStorage.setItem(JUST_SIGNED_IN, '1'); // land on the new person's Home
      } catch {
        /* ignore */
      }
      location.assign(r.role === 'process_server' ? '/field' : '/');
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="demo-switch" role="region" aria-label="Demo role switcher">
      <span className="demo-tag">DEMO</span>
      <label htmlFor="demo-role">View as</label>
      <select id="demo-role" value={data.current ?? ''} disabled={busy} onChange={(e) => change(e.target.value)}>
        {!data.current && <option value="">Choose a role…</option>}
        {data.roles.map((r) => (
          <option key={r.key} value={r.key}>
            {r.label}
          </option>
        ))}
      </select>
      <a className="demo-route" href="/route-demo">
        Route demo
      </a>
      {busy && <span className="demo-busy">Switching…</span>}
      {err && <span className="demo-err" role="alert">{err}</span>}
    </div>
  );
}
